import { fetchBlob, fetchJson } from "../lib/api";
import { isDemoMode } from "../lib/env";
import { recordsCsv, saveBlob } from "../lib/fileDownload";

export type VarianceSort =
  | "overpay_desc"
  | "overpay_asc"
  | "delta_pct_desc"
  | "delta_pct_asc"
  | "doc_date_desc"
  | "doc_date_asc"
  | "net_price_desc"
  | "net_price_asc";

export type VarianceGroupBy =
  | "project,delivery_address"
  | "delivery_address,project"
  | "project"
  | "delivery_address"
  | "manufacturer"
  | "manufacturer,project"
  | "manufacturer,delivery_address"
  | "article"
  // Die größten Preisabweichungen: ein Artikel bei einem Großhändler auf
  // einer Kostenstelle, damit jede Zeile eine Aufgabe ist (#543).
  | "article,supplier,project"
  | "supplier";

export type VarianceGroupedSort = "overpay_desc" | "spend_desc" | "lines_desc" | "max_delta_desc";

export type PriceAnomalyStatus = "open" | "in_review" | "resolved";

export type PriceVarianceRow = {
  article_group_key: string;
  primary_article_code: string | null;
  manufacturer_id: string | null;
  manufacturer_name: string | null;
  brand_name: string | null;
  product_family: string | null;
  doc_date: string | null;
  doc_type: string | null;
  doc_number: string | null;
  invoice_doc_id: string | null;
  line_pos: number | string | null;
  price_anomaly_status: PriceAnomalyStatus | null;
  project_key: string | null;
  supplier: string | null;
  online_user_email: string | null;
  online_user_name: string | null;
  qty: string | number | null;
  uom: string | null;
  net_unit_price: string | number | null;
  line_net_amount: string | number | null;
  baseline_avg_price: string | number | null;
  best_price: string | number | null;
  delta_abs: string | number | null;
  delta_pct: string | number | null;
  delta_best_abs: string | number | null;
  delta_best_pct: string | number | null;
  overpay_abs: string | number | null;
  canonical_baseline_kind: "12m" | "24m" | "alltime" | null;
  canonical_baseline_price: string | number | null;
  canonical_baseline_line_count: string | number | null;
  canonical_baseline_document_count: string | number | null;
  canonical_baseline_first_date: string | null;
  canonical_baseline_last_date: string | null;
  canonical_delta_pct: string | number | null;
  canonical_overpay_abs: string | number | null;
  price_anomaly_rule_revision: number;
  price_anomaly_comparison_basis:
    | "prior_12m_fallback"
    | "prior_24m_fallback"
    | "prior_all_history";
  price_anomaly_threshold_pct: string | number;
  price_anomaly_minimum_reference_count?: number;
  price_anomaly_minimum_overpay_abs: string | number | null;
  avg_net_unit_price_alltime: string | number | null;
  avg_net_unit_price_12m: string | number | null;
  avg_net_unit_price_24m: string | number | null;
  delta_pct_alltime: string | number | null;
  delta_pct_12m: string | number | null;
  delta_pct_24m: string | number | null;
  net_price_spread_pct_12m: string | number | null;
  delivery_address_key: string | null;
  delivery_address_display: string | null;
  delivery_is_warehouse: boolean | null;
  description: string | null;
  source_kind: string | null;
  source_file: string | null;
  source_record_row: string | number | null;
  raw_record: string | null;
};

export type PriceVarianceResponse = {
  total: number;
  limit: number;
  offset: number;
  sort: VarianceSort;
  items: PriceVarianceRow[];
};

export type VarianceGroupRow = {
  project_key?: string | null;
  delivery_address_key?: string | null;
  delivery_address_display?: string | null;
  delivery_is_warehouse?: boolean | null;
  manufacturer_id?: string | null;
  manufacturer_name?: string | null;
  // Article-Achsenfelder — nur wenn `group_by` `article` enthält.
  article_group_key?: string | null;
  primary_article_code?: string | null;
  description?: string | null;
  brand_name?: string | null;
  product_family?: string | null;
  // Supplier-Achsenfeld — nur wenn `group_by` `supplier` enthält.
  supplier?: string | null;
  line_count: number | null;
  total_qty?: string | number | null;
  total_spend: string | number | null;
  total_overpay: string | number | null;
  overpay_line_count: number | null;
  avg_delta_pct: string | number | null;
  /** Highest deviation of a dearer purchase in the group (Analyse, Top 10). */
  max_delta_pct?: string | number | null;
  first_doc_date: string | null;
  last_doc_date: string | null;
};

export type VarianceGroupedResponse = {
  group_by: string[];
  items: VarianceGroupRow[];
};

export type UserVarianceRow = {
  online_user_email: string;
  online_user_name: string | null;
  line_count: number | null;
  article_count: number | null;
  project_count: number | null;
  overpay_line_count: number | null;
  total_spend: string | number | null;
  avg_delta_pct: string | number | null;
  weighted_avg_delta_abs: string | number | null;
  total_overpay: string | number | null;
  first_doc_date: string | null;
  last_doc_date: string | null;
};

export type UserVarianceListResponse = {
  total: number;
  limit: number;
  offset: number;
  items: UserVarianceRow[];
};

export type PriceVarianceFilters = {
  from?: string;
  to?: string;
  online_user?: string;
  project?: string[];
  article?: string[];
  manufacturer_id?: string[];
  supplier?: string[];
  price_anomaly_status?: PriceAnomalyStatus;
  min_delta_pct?: number;
  min_overpay_abs?: number;
  positive_only?: boolean;
  min_reference_count?: number;
  reference_quality?: "sufficient" | "insufficient";
  delivery_address_key?: string[];
  is_warehouse?: boolean;
};

function toSearchParams(
  filters: PriceVarianceFilters & {
    sort?: VarianceSort | VarianceGroupedSort;
    limit?: number;
    offset?: number;
    group_by?: VarianceGroupBy;
  },
): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.online_user) params.set("online_user", filters.online_user);
  if (filters.project?.length) {
    for (const key of filters.project) params.append("project", key);
  }
  if (filters.article?.length) {
    for (const key of filters.article) params.append("article", key);
  }
  if (filters.manufacturer_id?.length) {
    for (const id of filters.manufacturer_id) params.append("manufacturer_id", id);
  }
  if (filters.supplier?.length) {
    for (const id of filters.supplier) params.append("supplier", id);
  }
  if (filters.price_anomaly_status) {
    params.set("price_anomaly_status", filters.price_anomaly_status);
  }
  if (filters.min_delta_pct != null) {
    params.set("min_delta_pct", String(filters.min_delta_pct));
  }
  if (filters.min_overpay_abs != null) {
    params.set("min_overpay_abs", String(filters.min_overpay_abs));
  }
  if (filters.positive_only != null) {
    params.set("positive_only", filters.positive_only ? "true" : "false");
  }
  if (filters.reference_quality) params.set("reference_quality", filters.reference_quality);
  if (filters.min_reference_count != null) {
    params.set("min_reference_count", String(filters.min_reference_count));
  }
  if (filters.delivery_address_key?.length) {
    for (const key of filters.delivery_address_key) {
      params.append("delivery_address_key", key);
    }
  }
  if (filters.is_warehouse != null) {
    params.set("is_warehouse", filters.is_warehouse ? "true" : "false");
  }
  if (filters.sort) params.set("sort", filters.sort);
  if (filters.group_by) params.set("group_by", filters.group_by);
  if (filters.limit != null) params.set("limit", String(filters.limit));
  if (filters.offset != null) params.set("offset", String(filters.offset));
  return params;
}

export async function fetchPriceVariance(
  filters: PriceVarianceFilters & {
    sort?: VarianceSort;
    limit?: number;
    offset?: number;
  },
): Promise<PriceVarianceResponse> {
  const params = toSearchParams(filters);
  return fetchJson<PriceVarianceResponse>(
    `/api/reports/price-variance?${params.toString()}`,
  );
}

export type ReferenceQualitySummary = Record<"sufficient" | "insufficient", {
  count: number;
  difference: string;
  unknown_amount_count: number;
}>;

export function fetchReferenceQualitySummary(filters: PriceVarianceFilters): Promise<ReferenceQualitySummary> {
  const params = toSearchParams(filters);
  params.set("format", "summary");
  return fetchJson<ReferenceQualitySummary>(`/api/reports/price-variance?${params}`);
}


export function priceVarianceCsvUrl(filters: PriceVarianceFilters & { sort?: VarianceSort }): string {
  const params = toSearchParams(filters);
  params.set("format", "csv");
  return `/api/reports/price-variance?${params.toString()}`;
}

const PRICE_VARIANCE_CSV_NAME = "price-variance.csv";
const DEMO_EXPORT_PAGE = 500;

/**
 * „Export für Excel“ der Positionen (#632-Review R2): in der App ein
 * authentifizierter Download mit denselben Filtern und derselben Sortierung,
 * in der Demo aus dem lokalen Beispieldatensatz – ohne Server.
 */
export async function downloadPriceVarianceCsv(filters: PriceVarianceFilters & { sort?: VarianceSort }): Promise<void> {
  if (isDemoMode()) {
    const rows: PriceVarianceRow[] = [];
    for (let offset = 0; ; offset += DEMO_EXPORT_PAGE) {
      const page = await fetchPriceVariance({ ...filters, limit: DEMO_EXPORT_PAGE, offset });
      rows.push(...page.items);
      if (!page.items.length || rows.length >= page.total) break;
    }
    saveBlob(new Blob([recordsCsv(rows as unknown as Record<string, unknown>[])], { type: "text/csv;charset=utf-8" }), PRICE_VARIANCE_CSV_NAME);
    return;
  }
  saveBlob(await fetchBlob(priceVarianceCsvUrl(filters), "text/csv"), PRICE_VARIANCE_CSV_NAME);
}

export async function fetchPriceVarianceGrouped(
  filters: PriceVarianceFilters & {
    group_by: VarianceGroupBy;
    sort?: VarianceGroupedSort;
    limit?: number;
    offset?: number;
  },
): Promise<VarianceGroupedResponse> {
  const params = toSearchParams(filters);
  return fetchJson<VarianceGroupedResponse>(
    `/api/reports/price-variance/grouped?${params.toString()}`,
  );
}

export async function fetchUserVariance(
  limit = 50,
  offset = 0,
): Promise<UserVarianceListResponse> {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  return fetchJson<UserVarianceListResponse>(
    `/api/reports/users/variance?${params.toString()}`,
  );
}


/** Einkaufsvolumen je Monat (Analyse, Reiter „Einkaufsvolumen“). */
export type PurchasesMonthlyRow = {
  month: string;
  manufacturer_id?: string | null;
  manufacturer_name?: string | null;
  supplier?: string | null;
  line_count: number;
  document_count: number;
  article_count: number;
  total_spend: string | number;
  total_overpay: string | number;
};

export type PurchasesMonthlyFilters = {
  from?: string;
  to?: string;
  supplier?: string[];
  manufacturer_id?: string[];
  group_by?: "manufacturer" | "supplier";
  /** Applies to the Mehrkosten column only; spend counts every line. */
  reference_quality?: "sufficient" | "insufficient";
};

export async function fetchPurchasesMonthly(
  filters: PurchasesMonthlyFilters,
): Promise<{ group_by: string | null; data_until: string | null; items: PurchasesMonthlyRow[] }> {
  const params = new URLSearchParams();
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  for (const value of filters.supplier ?? []) params.append("supplier", value);
  for (const value of filters.manufacturer_id ?? []) params.append("manufacturer_id", value);
  if (filters.group_by) params.set("group_by", filters.group_by);
  if (filters.reference_quality) params.set("reference_quality", filters.reference_quality);
  return fetchJson(`/api/reports/purchases/monthly?${params.toString()}`);
}
